import React from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  LinearProgress,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import noImage from '../../images/noImage.png';

function formatDateTime(value) {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '—';
  return parsed.toLocaleString('ro-RO', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function ClientsRecentRegistrations({ clients, status, onRetry }) {
  const rows = clients || [];
  const loading = status === 'loading';
  const error = status === 'error';
  const hasData = Array.isArray(clients);

  return (
    <Card
      component="section"
      aria-labelledby="recent-registrations-heading"
      sx={{
        width: '100%',
        height: '100%',
        minHeight: 390,
        borderRadius: { xs: '14px', sm: '16px' },
        border: '1px solid rgba(15, 23, 42, 0.06)',
        boxShadow: '0 4px 14px rgba(15, 23, 42, 0.08)',
        overflow: 'hidden',
      }}
    >
      {loading && hasData && <LinearProgress aria-label="Refreshing recent registrations" />}
      <CardContent sx={{ p: '0 !important' }}>
        <Box
          sx={{
            px: { xs: 1.5, sm: 2 },
            py: { xs: 1.25, sm: 1.5 },
            borderBottom: '1px solid rgba(15, 23, 42, 0.06)',
            background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
          }}
        >
          <Typography id="recent-registrations-heading" sx={{ color: '#111827', fontWeight: 800, fontSize: '1.05rem' }}>
            Recent registrations
          </Typography>
          <Typography sx={{ mt: 0.2, color: '#64748b', fontSize: '0.8rem' }}>
            Most recently registered clients
          </Typography>
        </Box>

        {error && !hasData ? (
          <Alert
            severity="error"
            action={(
              <Button color="inherit" size="small" startIcon={<RefreshRoundedIcon />} onClick={onRetry}>
                Retry
              </Button>
            )}
            sx={{ m: 1.5 }}
          >
            Recent registrations could not be loaded.
          </Alert>
        ) : loading && !hasData ? (
          <Box sx={{ p: 1.5 }}>
            <Skeleton variant="rounded" height={250} />
          </Box>
        ) : (
          <TableContainer sx={{ maxHeight: 440 }}>
            <Table stickyHeader size="small" aria-label="Recent client registrations">
              <TableHead>
                <TableRow>
                  {['Client', 'Name', 'County', 'Registered', 'Credit'].map((label) => (
                    <TableCell
                      key={label}
                      align={label === 'Credit' ? 'right' : 'left'}
                      sx={{
                        bgcolor: '#f8fafc',
                        color: '#334155',
                        fontWeight: 800,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 5, color: '#64748b' }}>
                      No recent registrations.
                    </TableCell>
                  </TableRow>
                ) : rows.map((client, index) => (
                  <TableRow hover key={client.id || client.iref || index}>
                    <TableCell sx={{ width: 64 }}>
                      <Avatar
                        src={client.image || noImage}
                        alt=""
                        variant="rounded"
                        sx={{ width: 34, height: 34, borderRadius: '10px' }}
                      />
                    </TableCell>
                    <TableCell sx={{ minWidth: 180, fontWeight: 650, color: '#111827' }}>
                      {client.name || '—'}
                    </TableCell>
                    <TableCell sx={{ minWidth: 130, color: '#334155' }}>
                      {client.county || '—'}
                    </TableCell>
                    <TableCell sx={{ minWidth: 170, color: '#334155', fontVariantNumeric: 'tabular-nums' }}>
                      {formatDateTime(client.createdAt || client.created_on)}
                    </TableCell>
                    <TableCell align="right" sx={{ color: '#334155', fontVariantNumeric: 'tabular-nums', fontWeight: 650 }}>
                      {Number(client.credit || 0).toLocaleString('ro-RO', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
    </Card>
  );
}

export default ClientsRecentRegistrations;
